import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Download, Plus, Edit3, Trash2, Save, X, RefreshCw, Check, Undo2, Quote } from 'lucide-react';
import {
  getToken, clearToken, getMe,
  listEnquiries, listSubscribers, listBlogs, listNews, listClients,
  enquiriesUnreadCount, updateEnquiry,
  createBlog, updateBlog, deleteBlog,
  createNews, updateNews, deleteNews,
  createClient, updateClient, deleteClient, reorderClients,
  exportEnquiries, exportSubscribers,
  generateImage,
} from './api';
import ImageUpload from './ImageUpload';
import ClientEditor from './ClientEditor';
import SortableClientGrid from './SortableClientGrid';
import TestimonialsTab from './TestimonialsTab';

const TABS = [
  { id: 'enquiries', label: 'Enquiries' },
  { id: 'subscribers', label: 'Subscribers' },
  { id: 'blogs', label: 'The Green Room' },
  { id: 'news', label: 'News' },
  { id: 'clients', label: 'Clients' },
  { id: 'testimonials', label: 'Quotes' },
];

export default function AdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState('enquiries');
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const refreshUnread = () => {
    enquiriesUnreadCount().then(setUnread).catch(() => {});
  };

  useEffect(() => {
    if (!getToken()) return;
    getMe().then((a) => { setAdmin(a); setLoading(false); }).catch(() => setLoading(false));
    refreshUnread();
  }, []);

  if (!getToken()) return <Navigate to="/admin/login" replace />;
  if (loading) return <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center text-[#6b6b6b] text-sm">Loading…</div>;

  const logout = () => { clearToken(); toast.success('Signed out.'); navigate('/admin/login', { replace: true }); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#faf8f4]">
      <header className="bg-[#002b5c] text-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div>
            <p className="eyebrow text-[#C5A059]">David Ball Management · Admin</p>
            <p className="mt-2 text-sm text-white/70">{admin?.email}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white text-white px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </div>
        <nav className="max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-wrap gap-8 border-t border-white/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`py-5 text-[11px] tracking-[0.24em] uppercase transition-colors inline-flex items-center gap-2 ${tab === t.id ? 'text-[#C5A059] border-b-2 border-[#C5A059]' : 'text-white/70 hover:text-white'}`}
            >
              {t.label}
              {t.id === 'enquiries' && unread > 0 && (
                <span
                  data-testid="enquiries-badge"
                  className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#C5A059] text-[#001a38] text-[10px] font-heading tracking-normal"
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-12">
        {tab === 'enquiries' && <EnquiriesTab refreshUnread={refreshUnread} />}
        {tab === 'subscribers' && <SubscribersTab />}
        {tab === 'blogs' && <BlogsTab />}
        {tab === 'news' && <NewsTab />}
        {tab === 'clients' && <ClientsTab />}
        {tab === 'testimonials' && <TestimonialsTab />}
      </main>
    </div>
  );
}

/* ---------- Enquiries ---------- */
function EnquiriesTab({ refreshUnread }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); listEnquiries().then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const toggleHandled = async (row) => {
    try {
      const updated = await updateEnquiry(row.id, !row.handled);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...updated } : r)));
      refreshUnread?.();
      toast.success(updated.handled ? 'Marked as handled.' : 'Reopened.');
    } catch {
      toast.error('Could not update. Please try again.');
    }
  };

  return (
    <section>
      <TabHeader
        title="Enquiries"
        count={rows.length}
        onRefresh={() => { load(); refreshUnread?.(); }}
        onExport={() => exportEnquiries().catch(() => toast.error('Export failed'))}
        exportLabel="Export CSV"
      />
      {loading ? <SkeletonRows /> : (
        <div className="space-y-3">
          {rows.length === 0 && <EmptyState label="No enquiries yet." />}
          {rows.map((r) => (
            <details
              key={r.id}
              className={`bg-white rounded-xl border transition-colors ${r.handled ? 'border-black/5 opacity-70' : 'border-black/8 hover:border-[#C5A059]/40'}`}
            >
              <summary className="cursor-pointer p-5 grid grid-cols-1 md:grid-cols-[auto,140px,1fr,1fr,140px,1fr] gap-3 items-center">
                <span
                  className={`w-2 h-2 rounded-full ${r.handled ? 'bg-black/15' : 'bg-[#C5A059]'}`}
                  aria-label={r.handled ? 'handled' : 'new'}
                />
                <span className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{formatDate(r.created_at)}</span>
                <span className={`font-heading uppercase ${r.handled ? 'text-[#6b6b6b] line-through decoration-[#C5A059]/40' : 'text-[#002b5c]'}`}>{r.name}</span>
                <span className="text-sm text-[#4a4a4a] truncate">{r.email}</span>
                <span className="text-[11px] tracking-[0.22em] uppercase text-[#C5A059]">{r.enquiry_type}</span>
                <span className="text-sm text-[#4a4a4a] truncate">{r.subject || r.message?.slice(0, 60) || '—'}</span>
              </summary>
              <div className="border-t border-black/5 p-5 space-y-4 text-sm text-[#333]">
                {r.phone && <p><strong>Phone:</strong> <a href={`tel:${r.phone}`} className="gold-underline">{r.phone}</a></p>}
                <p><strong>Reply to:</strong> <a href={`mailto:${r.email}`} className="gold-underline">{r.email}</a></p>
                <p className="whitespace-pre-line leading-relaxed">{r.message}</p>
                <div className="pt-4 flex flex-wrap items-center gap-3 border-t border-black/5">
                  <button
                    data-testid={`enquiry-toggle-${r.id}`}
                    onClick={() => toggleHandled(r)}
                    className={
                      r.handled
                        ? 'inline-flex items-center gap-2 rounded-full border border-black/15 text-[#6b6b6b] hover:text-[#002b5c] hover:border-[#002b5c] px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase transition-colors'
                        : 'inline-flex items-center gap-2 rounded-full bg-[#002b5c] text-white px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase hover:bg-[#001a38] transition-colors'
                    }
                  >
                    {r.handled ? <><Undo2 size={14} /> Reopen</> : <><Check size={14} /> Mark as handled</>}
                  </button>
                  {r.handled && r.handled_at && (
                    <span className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                      Handled {formatDate(r.handled_at)}
                    </span>
                  )}
                </div>
              </div>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- Subscribers ---------- */
function SubscribersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); listSubscribers().then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <section>
      <TabHeader
        title="Newsletter subscribers"
        count={rows.length}
        onRefresh={load}
        onExport={() => exportSubscribers().catch(() => toast.error('Export failed'))}
        exportLabel="Export CSV"
      />
      {loading ? <SkeletonRows /> : (
        <div className="bg-white rounded-xl border border-black/8 overflow-hidden">
          {rows.length === 0 && <EmptyState label="No subscribers yet." />}
          {rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-[#faf8f4] text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">
                <tr>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Email</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.email || `row-${i}`} className="border-t border-black/5 hover:bg-[#faf8f4] transition-colors">
                    <td className="p-4 text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{formatDate(r.created_at)}</td>
                    <td className="p-4 font-heading uppercase text-[#002b5c]">{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className="p-4 text-[#4a4a4a]"><a href={`mailto:${r.email}`} className="gold-underline">{r.email}</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------- Blogs ---------- */
const EMPTY_BLOG = {
  slug: '', title: '', excerpt: '', body: '',
  cover: '', category: 'Auditions',
  author: 'David Ball', read_time: '4 min',
  published_at: new Date().toISOString().slice(0, 10),
  featured: false,
};

function BlogsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id

  const load = () => { setLoading(true); listBlogs().then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  if (editing) {
    const initial = editing === 'new' ? EMPTY_BLOG : rows.find((r) => r.id === editing) || EMPTY_BLOG;
    return (
      <PostEditor
        title={editing === 'new' ? 'New Green Room article' : 'Edit Green Room article'}
        initial={initial}
        fields={['slug', 'title', 'category', 'author', 'read_time', 'published_at', 'cover', 'excerpt', 'body', 'featured']}
        enableAiCover
        onCancel={() => setEditing(null)}
        onSave={async (values) => {
          try {
            if (editing === 'new') await createBlog(values); else await updateBlog(editing, values);
            toast.success('Saved.');
            setEditing(null); load();
          } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
        }}
      />
    );
  }

  return (
    <section>
      <TabHeader
        title="The Green Room"
        count={rows.length}
        onRefresh={load}
        onNew={() => setEditing('new')}
        newLabel="New article"
      />
      {loading ? <SkeletonRows /> : (
        <PostList
          rows={rows}
          onEdit={(id) => setEditing(id)}
          onDelete={async (id) => {
            if (!window.confirm('Delete this article?')) return;
            try { await deleteBlog(id); toast.success('Deleted.'); load(); } catch { toast.error('Delete failed'); }
          }}
        />
      )}
    </section>
  );
}

/* ---------- News ---------- */
const EMPTY_NEWS = {
  slug: '', title: '', excerpt: '', body: '',
  cover: '', category: 'Signing',
  published_at: new Date().toISOString().slice(0, 10),
};

function NewsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => { setLoading(true); listNews().then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  if (editing) {
    const initial = editing === 'new' ? EMPTY_NEWS : rows.find((r) => r.id === editing) || EMPTY_NEWS;
    return (
      <PostEditor
        title={editing === 'new' ? 'New news item' : 'Edit news item'}
        initial={initial}
        fields={['slug', 'title', 'category', 'published_at', 'cover', 'excerpt', 'body']}
        onCancel={() => setEditing(null)}
        onSave={async (values) => {
          try {
            if (editing === 'new') await createNews(values); else await updateNews(editing, values);
            toast.success('Saved.');
            setEditing(null); load();
          } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
        }}
      />
    );
  }

  return (
    <section>
      <TabHeader
        title="News"
        count={rows.length}
        onRefresh={load}
        onNew={() => setEditing('new')}
        newLabel="New item"
      />
      {loading ? <SkeletonRows /> : (
        <PostList
          rows={rows}
          onEdit={(id) => setEditing(id)}
          onDelete={async (id) => {
            if (!window.confirm('Delete this news item?')) return;
            try { await deleteNews(id); toast.success('Deleted.'); load(); } catch { toast.error('Delete failed'); }
          }}
        />
      )}
    </section>
  );
}

/* ---------- Shared bits ---------- */
export function TabHeader({ title, count, onRefresh, onExport, onNew, exportLabel, newLabel }) {
  return (
    <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
      <div>
        <p className="eyebrow text-[#C5A059]">Data</p>
        <h2 className="font-heading uppercase text-3xl md:text-4xl text-[#002b5c] mt-3 leading-tight">{title}</h2>
        <p className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{count} record{count === 1 ? '' : 's'}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onRefresh} className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] px-4 py-2.5 rounded-full border border-black/15 hover:border-[#002b5c] transition-colors">
          <RefreshCw size={13} /> Refresh
        </button>
        {onExport && (
          <button onClick={onExport} data-testid="admin-export" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] px-4 py-2.5 rounded-full border border-black/15 hover:border-[#002b5c] transition-colors">
            <Download size={13} /> {exportLabel}
          </button>
        )}
        {onNew && (
          <button onClick={onNew} data-testid="admin-new" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[#002b5c] text-white px-5 py-2.5 rounded-full hover:bg-[#001a38] transition-colors">
            <Plus size={13} /> {newLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function PostList({ rows, onEdit, onDelete }) {
  if (rows.length === 0) return <EmptyState label="No items yet — click New to add one." />;
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.id} className="bg-white border border-black/8 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4 hover:border-[#C5A059]/40 transition-colors">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{r.category} · {formatDate(r.published_at)}</p>
              {r.featured && (
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#C5A059] border border-[#C5A059]/40 rounded-full px-2 py-0.5">
                  Featured
                </span>
              )}
              {r.newsletter_status && NEWSLETTER_LABELS[r.newsletter_status] && (
                <span data-testid={`newsletter-status-${r.slug}`} className={`text-[10px] tracking-[0.2em] uppercase rounded-full px-2 py-0.5 border ${r.newsletter_status === 'sent' ? 'text-emerald-700 border-emerald-700/30' : r.newsletter_status === 'pending' || r.newsletter_status === 'sending' ? 'text-[#6b6b6b] border-black/15' : 'text-red-600 border-red-600/30'}`} title={r.newsletter_error || undefined}>
                  {NEWSLETTER_LABELS[r.newsletter_status]}
                </span>
              )}
            </div>
            <h3 className="font-heading uppercase text-lg text-[#002b5c] mt-1 truncate">{r.title}</h3>
            <p className="text-xs text-[#6b6b6b] mt-1 font-mono truncate">/{r.slug}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit(r.id)} className="p-2.5 rounded-full border border-black/15 hover:border-[#002b5c] text-[#002b5c] transition-colors" aria-label="Edit">
              <Edit3 size={15} />
            </button>
            <button onClick={() => onDelete(r.id)} className="p-2.5 rounded-full border border-black/15 hover:border-red-500 hover:text-red-500 text-[#6b6b6b] transition-colors" aria-label="Delete">
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PostEditor({ title, initial, fields, onCancel, onSave, enableAiCover }) {
  const [form, setForm] = useState({ ...initial });
  const [slugUnlocked, setSlugUnlocked] = useState(false);
  const isNew = !initial?.id;

  const slugify = (s) =>
    (s || '')
      .toLowerCase()
      .replace(/['".,!?()]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);

  const upd = (k, v) => {
    setForm((f) => {
      // On NEW posts, auto-sync slug ← title so the admin doesn't have to think about it
      if (k === 'title' && isNew) {
        return { ...f, title: v, slug: slugify(v) };
      }
      return { ...f, [k]: v };
    });
  };

  const isRequired = (k) => ['slug', 'title', 'excerpt', 'body', 'category', 'published_at'].includes(k);

  const submit = (e) => {
    e.preventDefault();
    for (const k of fields) {
      if (isRequired(k) && !String(form[k] ?? '').trim()) {
        toast.error(`Please fill in ${k.replace('_', ' ')}`);
        return;
      }
    }
    onSave(form);
  };

  // Callback for the AI generate button on the cover field — only relevant for blogs
  const generateCover = async () => {
    if (!form.title?.trim()) {
      toast.error('Please add a title before generating a cover.');
      return {};
    }
    return generateImage({
      prompt: form.excerpt || form.title,
      title: form.title,
      category: form.category,
    });
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-black/8 p-8 md:p-10 max-w-[900px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="eyebrow text-[#C5A059]">Editor</p>
          <h2 className="font-heading uppercase text-2xl md:text-3xl text-[#002b5c] mt-2 leading-tight">{title}</h2>
        </div>
        <button type="button" onClick={onCancel} className="p-2 rounded-full text-[#6b6b6b] hover:text-[#002b5c]">
          <X size={20} />
        </button>
      </div>

      <div className="space-y-6">
        {fields.map((k) => (
          <Field
            key={k}
            name={k}
            value={form[k]}
            onChange={(v) => upd(k, v)}
            required={isRequired(k)}
            onGenerate={enableAiCover && k === 'cover' ? generateCover : undefined}
            slugLocked={k === 'slug' && !isNew && !slugUnlocked}
            onUnlockSlug={k === 'slug' && !isNew ? () => setSlugUnlocked(true) : undefined}
          />
        ))}
      </div>

      <div className="mt-10 flex items-center gap-3">
        <button type="submit" data-testid="editor-save" className="inline-flex items-center gap-2 rounded-full bg-[#002b5c] text-white px-8 py-3.5 text-[11px] tracking-[0.24em] uppercase hover:bg-[#001a38] transition-colors">
          <Save size={14} /> Save
        </button>
        <button type="button" onClick={onCancel} className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b] hover:text-[#002b5c] px-4 py-3.5">
          Cancel
        </button>
      </div>
    </form>
  );
}

const NEWSLETTER_LABELS = {
  pending: 'Email waiting to send',
  sending: 'Emailing subscribers',
  sent: 'Emailed to subscribers',
  blocked: 'Email not sent',
  error: 'Email not sent',
};

function BodyField({ value, onChange, required }) {
  const ref = useRef(null);
  const insertQuote = () => {
    const el = ref.current;
    const text = value || '';
    const start = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    const selected = text.slice(start, end).trim() || 'Your highlighted line here';
    const before = text.slice(0, start).replace(/\s*$/, '');
    const after = text.slice(end).replace(/^\s*/, '');
    const block = `> ${selected.replace(/\n+/g, ' ')}`;
    const next = `${before}${before ? '\n\n' : ''}${block}${after ? '\n\n' : ''}${after}`;
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = before.length + (before ? 2 : 0) + block.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Label>Body</Label>
        <button type="button" onClick={insertQuote} data-testid="editor-insert-quote" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase rounded-full px-3 py-1.5 border border-[#C5A059]/50 text-[#002b5c] hover:bg-[#C5A059] hover:text-white transition-colors">
          <Quote size={12} /> Highlight quote
        </button>
      </div>
      <textarea
        ref={ref}
        data-testid="editor-body-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={14}
        required={required}
        placeholder={`## An example heading\n\nThis is an ordinary paragraph. Leave a blank line between blocks.\n\n### A sub-heading\n\n* First bullet point\n* Second bullet point\n* Third bullet point\n\n> A highlighted quote\n\nYou can also use **bold** or *italic* inline.`}
        className="mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-3 px-4 outline-none text-sm leading-relaxed font-mono"
      />
      <p className="mt-2 text-[11px] text-[#6b6b6b] leading-relaxed">
        <strong className="text-[#002b5c]">Formatting:</strong> <code>## Heading</code>, <code>### Sub-heading</code>, bullets with <code>*</code> <code>-</code> or <code>•</code>, <code>&gt; Highlighted quote</code>, <code>**bold**</code>, <code>*italic*</code> or <code>_italic_</code>. To highlight a quote, select a line and press <strong>Highlight quote</strong>. Always leave a blank line between paragraphs, headings and bullet lists.
      </p>
    </div>
  );
}

function Field({ name, value, onChange, required, onGenerate, slugLocked, onUnlockSlug }) {
  const label = name.replace('_', ' ');
  if (name === 'cover') {
    return (
      <ImageUpload
        label={label}
        value={value}
        onChange={onChange}
        onGenerate={onGenerate}
        hint={onGenerate
          ? "Upload, paste a URL, or click 'Generate with AI' to create an editorial cover from the title + category."
          : "Optional. Upload directly or paste a full https:// URL."}
      />
    );
  }
  if (name === 'body') return <BodyField value={value} onChange={onChange} required={required} />;
  if (name === 'excerpt') return (
    <div>
      <Label>{label}</Label>
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} required={required} maxLength={400} className="mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-3 px-4 outline-none text-sm leading-relaxed" />
      <p className="mt-1 text-[11px] text-[#6b6b6b]">{(value || '').length}/400</p>
    </div>
  );
  if (name === 'featured') return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#002b5c]" />
      <span className="text-sm text-[#333]">Featured (show at top of listings)</span>
    </label>
  );
  if (name === 'slug') {
    return (
      <div>
        <Label>slug{required && <span className="text-[#C5A059] ml-1">*</span>}</Label>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={!!slugLocked}
          className="mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-3 px-4 outline-none text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        />
        {slugLocked ? (
          <p className="mt-1 text-[11px] text-[#6b6b6b]">
            Slug locked to preserve Google indexing.{' '}
            <button type="button" onClick={onUnlockSlug} className="underline hover:text-[#002b5c]">
              Change anyway
            </button>
            {' '}<span className="text-red-600">(will break existing links)</span>
          </p>
        ) : (
          <p className="mt-1 text-[11px] text-[#6b6b6b]">
            URL segment for /green-room/<em>{value || 'your-slug'}</em>. Lowercase, hyphenated, unique.
          </p>
        )}
      </div>
    );
  }
  const type = name === 'published_at' ? 'date' : 'text';
  return (
    <div>
      <Label>{label}{required && <span className="text-[#C5A059] ml-1">*</span>}</Label>
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} required={required} className="mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-3 px-4 outline-none text-sm" />
    </div>
  );
}

/* ---------- Clients ---------- */
function ClientsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [q, setQ] = useState('');

  const load = () => { setLoading(true); listClients().then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  if (editing) {
    const initial = editing === 'new' ? undefined : rows.find((r) => r.id === editing);
    return (
      <ClientEditor
        initial={initial}
        onCancel={() => setEditing(null)}
        onSave={async (values) => {
          try {
            if (editing === 'new') await createClient(values);
            else await updateClient(editing, values);
            toast.success('Saved.');
            setEditing(null);
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
          }
        }}
      />
    );
  }

  const filtered = q
    ? rows.filter((r) => (r.name || '').toLowerCase().includes(q.toLowerCase()) || (r.slug || '').toLowerCase().includes(q.toLowerCase()))
    : rows;
  const handleReorder = async (next) => {
    setRows(next);
    try { await reorderClients(next.map((r) => r.id)); toast.success('Order saved.'); }
    catch { toast.error('Could not save the new order'); load(); }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete ${r.name}? This cannot be undone.`)) return;
    try { await deleteClient(r.id); toast.success('Deleted.'); load(); } catch { toast.error('Delete failed'); }
  };

  const renderCard = (r, handle) => (
    <ClientCard
      client={r}
      handle={handle}
      onEdit={() => setEditing(r.id)}
      onDelete={() => onDelete(r)}
    />
  );

  return (
    <section>
      <TabHeader
        title="Clients"
        count={filtered.length}
        onRefresh={load}
        onNew={() => setEditing('new')}
        newLabel="New client"
      />

      <div className="mb-6 flex flex-wrap items-center gap-4 justify-between">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name or slug…"
          data-testid="clients-filter-input"
          className="w-full max-w-sm bg-white border border-black/10 focus:border-[#002b5c] rounded-full py-2.5 px-4 outline-none text-sm"
        />
        <div className="flex flex-wrap items-center gap-4 text-xs text-[#6b6b6b]">
          <span>{q ? 'Clear the filter to reorder.' : 'Drag the handle to reorder the Clients page.'}</span>
        </div>
      </div>

      {loading ? <SkeletonRows /> : (
        filtered.length === 0 ? <EmptyState label="No matching clients." /> : (
          q ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map((r) => <React.Fragment key={r.id}>{renderCard(r, null)}</React.Fragment>)}
            </div>
          ) : (
            <SortableClientGrid rows={rows} onReorder={handleReorder} renderItem={renderCard} />
          )
        )
      )}
    </section>
  );
}

function ClientCard({ client, handle, onEdit, onDelete }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const src = client.headshot
    ? (client.headshot.startsWith('http') ? client.headshot : `${backendUrl}${client.headshot}`)
    : '';
  return (
    <div data-testid={`client-card-${client.slug}`} className="bg-white border border-black/8 rounded-xl overflow-hidden hover:border-[#C5A059]/40 transition-colors h-full">
      <div className="flex items-start gap-3 p-4">
        {handle && <div className="self-center">{handle}</div>}
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#faf8f4] shrink-0">
          {src ? <img src={src} alt={client.name} className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading uppercase text-lg text-[#002b5c] leading-tight truncate">{client.name}</h3>
          <p className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b] mt-1 truncate">/{client.slug}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <button onClick={onEdit} data-testid={`client-edit-${client.slug}`} className="p-2 rounded-full border border-black/15 hover:border-[#002b5c] text-[#002b5c] transition-colors" aria-label="Edit">
            <Edit3 size={14} />
          </button>
          <button onClick={onDelete} data-testid={`client-delete-${client.slug}`} className="p-2 rounded-full border border-black/15 hover:border-red-500 hover:text-red-500 text-[#6b6b6b] transition-colors" aria-label="Delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function Label({ children }) {
  return <label className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{children}</label>;
}

export function EmptyState({ label }) {
  return <div className="bg-white border border-black/8 rounded-xl p-16 text-center text-[#6b6b6b] text-sm">{label}</div>;
}

export function SkeletonRows() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4].map((n) => (
        <div key={`skel-${n}`} className="h-16 bg-white/70 border border-black/8 rounded-xl animate-pulse" />
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
